/**
 * GAUGE CONVERSION UTILITIES
 * 
 * Converts between feet+inches (what pumpers read on gauges)
 * and total inches (what the database stores).
 * 
 * IMPORTANT: The database always stores total inches as a number.
 * These utilities are for UI display/input only.
 * 
 * @example
 * // Pumper reads: "4 feet 7 inches" on the gauge
 * // UI shows: [4] ft [7] in
 * // Database stores: 55 (total inches)
 * // Calculations use: 55 * bblPerInch
 */

export interface FeetInches {
  feet: number;
  inches: number;
}

/**
 * Convert total inches to feet and inches for display
 * 
 * @param totalInches - Total inches (from database or state)
 * @returns Object with feet and inches components
 * 
 * @example
 * inchesToFeetInches(55) // → { feet: 4, inches: 7 }
 * inchesToFeetInches(12) // → { feet: 1, inches: 0 }
 * inchesToFeetInches(0)  // → { feet: 0, inches: 0 }
 * inchesToFeetInches('')  // → { feet: 0, inches: 0 }
 */
export function inchesToFeetInches(totalInches: number | string): FeetInches {
  const total = typeof totalInches === 'string' 
    ? parseFloat(totalInches) || 0 
    : totalInches || 0;
  
  const feet = Math.floor(total / 12);
  const inches = Math.round((total % 12) * 10) / 10; // Keep 1 decimal for precision
  
  return { feet, inches };
}

/**
 * Convert feet and inches back to total inches for storage
 * 
 * @param feet - Feet component (whole number)
 * @param inches - Inches component (0-11.9)
 * @returns Total inches as a number
 * 
 * @example
 * feetInchesToTotalInches(4, 7)   // → 55
 * feetInchesToTotalInches(1, 0)   // → 12
 * feetInchesToTotalInches(0, 6)   // → 6
 * feetInchesToTotalInches('4', '7') // → 55 (handles string inputs)
 */
export function feetInchesToTotalInches(
  feet: number | string, 
  inches: number | string
): number {
  const ft = typeof feet === 'string' ? parseFloat(feet) || 0 : feet || 0;
  const inch = typeof inches === 'string' ? parseFloat(inches) || 0 : inches || 0;
  
  return (ft * 12) + inch;
}

/**
 * Format total inches as a display string
 * 
 * @param totalInches - Total inches
 * @returns Formatted string like "4' 7\"" or "55\""
 * 
 * @example
 * formatGaugeDisplay(55)  // → "4' 7\""
 * formatGaugeDisplay(12)  // → "1' 0\""
 * formatGaugeDisplay(6)   // → "6\""  (just inches if < 12)
 */
export function formatGaugeDisplay(totalInches: number | string): string {
  const total = typeof totalInches === 'string' 
    ? parseFloat(totalInches) || 0 
    : totalInches || 0;
  
  if (total === 0) return '0"';
  
  const { feet, inches } = inchesToFeetInches(total);
  
  if (feet === 0) {
    return `${inches}"`;
  }
  
  return `${feet}' ${inches}"`;
}

/**
 * Validate inches component is within range (0-11.9)
 * 
 * @param inches - Inches value to validate
 * @returns True if valid (0-11.9), false otherwise
 */
export function isValidInches(inches: number | string): boolean {
  const val = typeof inches === 'string' ? parseFloat(inches) : inches;
  if (isNaN(val)) return false;
  return val >= 0 && val < 12;
}

/**
 * Validate feet is a non-negative number
 * 
 * @param feet - Feet value to validate
 * @returns True if valid (>= 0), false otherwise
 */
export function isValidFeet(feet: number | string): boolean {
  const val = typeof feet === 'string' ? parseFloat(feet) : feet;
  if (isNaN(val)) return false;
  return val >= 0;
}

/**
 * Parse voice input for feet and inches
 * Handles natural speech patterns like:
 * - "4 feet 7 inches"
 * - "4 foot 7"
 * - "four feet seven inches"
 * 
 * @param voiceInput - Raw voice transcription
 * @returns Total inches, or null if not parseable
 */
export function parseVoiceFeetInches(voiceInput: string): number | null {
  const input = voiceInput.toLowerCase().trim();
  
  // Pattern: "X feet Y inches" or "X foot Y inch" or "X feet Y"
  const feetInchesPattern = /(\d+)\s*(?:feet|foot|ft|')\s*(\d+)?(?:\s*(?:inches?|in|"))?/i;
  const match = input.match(feetInchesPattern);
  
  if (match) {
    const feet = parseInt(match[1]) || 0;
    const inches = parseInt(match[2]) || 0;
    return feetInchesToTotalInches(feet, inches);
  }
  
  // Pattern: Just inches "55 inches" or "55""
  const inchesOnlyPattern = /(\d+)\s*(?:inches?|in|")/i;
  const inchesMatch = input.match(inchesOnlyPattern);
  
  if (inchesMatch) {
    return parseInt(inchesMatch[1]) || 0;
  }
  
  return null;
}
